import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, merge, of, Subscription } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { OrganisationModel, OrganisationDeletedFromAdminTrash, OrganisationDeletedFromTrash, OrganisationOnServerAdminRestored, OrganisationOnServerRestored, OrganisationsDataSource, OrganisationService, OrganisationTrashFlushed, OrganisationActionToggleLoading, OrganisationDeleted, OrganisationsPageRequested, selectTrashedOrganisationCount, selectAdminTrashedOrganisationCount, OrganisationUpdated } from 'src/app/core/linked-tables';
import { AppState } from 'src/app/core/reducers';
import { LayoutUtilsService, QueryParamsModel } from 'src/app/core/_base/crud';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { cloneDeep } from 'lodash';
import { hasPermission } from 'src/app/core/mad-auth/mad-auth.store';

@Component({
  selector: 'kt-organisations-list',
  templateUrl: './organisations-list.component.html',
  styleUrls: ['./organisations-list.component.scss']
})
export class OrganisationsListComponent implements OnInit {
  dataSource: OrganisationsDataSource;
  displayedColumns = ['id', 'name', 'is_hidden', 'actions'];
  trashCnt = 0;
  adminTrashCnt = 0;

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild('sort1', {static: true}) sort: MatSort;
  // Filter fields
  @ViewChild('searchInput', {static: true}) searchInput: ElementRef;
  // Selection
  mode = 'NEW';
  selection = new SelectionModel<OrganisationModel>(true, []);
  canAccessAdminTrash$ = this.store.pipe(select(hasPermission(['admin_trash'])))

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              public dialog: MatDialog,
              public service: OrganisationService,
              private layoutUtilsService: LayoutUtilsService,
              private translate: TranslateService,
              private store: Store<AppState>) {
  }

  ngOnInit(): void {
        this.paginator._intl.itemsPerPageLabel = 'Display';
        // If the user changes the sort order, reset back to the first page.
        const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.subscriptions.push(sortSubscription);

        /* Data load will be triggered in two cases:
        - when a pagination event occurs => this.paginator.page
        - when a sort event occurs => this.sort.sortChange
        **/
        const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).pipe(
            tap(() => this.loadList())
        )
            .subscribe();
        this.subscriptions.push(paginatorSubscriptions);

        // Filtration, bind to searchInput
        const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
            // tslint:disable-next-line:max-line-length
            debounceTime(50), // The user can type quite quickly in the input box, and that could trigger a lot of server requests. With this operator, we are limiting the amount of server requests emitted to a maximum of one every 150ms
            distinctUntilChanged(), // This operator will eliminate duplicate values
            tap(() => {
                
                this.paginator.pageIndex = 0;
                this.loadList();
            })
        )
            .subscribe();
        this.subscriptions.push(searchSubscription);

        // Init DataSource
        this.dataSource = new OrganisationsDataSource(this.store);
        const entitiesSubscription = this.dataSource.entitySubject.pipe(
            skip(1),
            distinctUntilChanged()
        ).subscribe(res => {});
        this.subscriptions.push(entitiesSubscription);

        // trash count

        const selectTrashedSubscription = this.store.pipe(
            select(selectTrashedOrganisationCount)
        ).subscribe(res => {
            this.trashCnt = res;
        });
        this.subscriptions.push(selectTrashedSubscription);

        const selectAdminTrashedSubscription = this.store.pipe(
            select(selectAdminTrashedOrganisationCount)
        ).subscribe(res => {
            this.adminTrashCnt = res;
        });
        this.subscriptions.push(selectAdminTrashedSubscription);

        // First load
        of(undefined).pipe(take(1), delay(1000)).subscribe(() => { // Remove this line, just loading imitatio
            this.loadList();
        }); // Remove this line, just loading imitation
  }

  addItem() {
      this.editItem(null);
  }

  ngOnDestroy() {
      this.subscriptions.forEach(el => el.unsubscribe());
  }

  loadList() {
      this.selection.clear();
      const queryParams = new QueryParamsModel(
          this.filterConfiguration(),
          this.sort.direction,
          this.sort.active,
          this.paginator.pageIndex + 1,
          this.paginator.pageSize
      );
      this.store.dispatch(new OrganisationsPageRequested({page: queryParams}));
      this.selection.clear();
  }

  filterConfiguration(): any {
      const filter: any = {
        with_hidden: true
      };
      const searchText: string = this.searchInput.nativeElement.value;

      filter.name = searchText;
      if (!searchText) {
          return filter;
      }
      return filter;
  }

  /**
   * Returns object for filter
   */
  resetFilter(): any {
      this.searchInput.nativeElement.value = '';
      this.loadList();
  }

  /**
   * Show Edit subTypeCategory dialog and save after success close result
   * @param subTypeCategory: ChimneyStack
   */
  editItem(item) {
      if (item) {
          this.router.navigate(['edit', item.id], {relativeTo: this.activatedRoute});
      } else {
          this.router.navigate(['add'], {relativeTo: this.activatedRoute});
      }
  }

  deleteItem (_item: OrganisationModel) {
      const _title = this.translate.instant('ORGANISATION.LIST.DIALOG.DELETE.TITLE');
      const _description: string = this.translate.instant('ORGANISATION.LIST.DIALOG.DELETE.DESCRIPTION', {name: _item.name});
      const _waitDesciption =  this.translate.instant('ORGANISATION.LIST.DIALOG.DELETE.WAIT_DESCRIPTION');

      const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
      dialogRef.afterClosed().subscribe(res => {
          if (!res) {
              return;
          }

          this.store.dispatch(new OrganisationDeleted({id: _item.id}));
      });
  }

  trash() {
      this.store.dispatch(new OrganisationActionToggleLoading({isLoading: true}));
      this.service.getTrashed().subscribe(res => {

          this.store.dispatch(new OrganisationActionToggleLoading({isLoading: false}));
          const items = [];
          res.data.forEach(elem => {
              items.push({
                  text: `${elem.name}`,
                  id: elem.id.toString(),
                  hasPermanentlyDelete: true,
                  date: elem.user_deleted,
                  deletedUser: (elem.user_deleted_by && elem.user_deleted_by.length > 0 ? elem.user_deleted_by : ''),
              });
          });
          this.show_trash(items);
      });
  }

  // Fetch admin trash data
  adminTrash() {
      this.store.dispatch(new OrganisationActionToggleLoading({isLoading: true}));
      this.service.getAdminTrashed().subscribe(res => {

          this.store.dispatch(new OrganisationActionToggleLoading({isLoading: false}));
          const items = [];
          res.data.forEach(elem => {
              items.push({
                  text: `${elem.name}`,
                  id: elem.id.toString(),
                  hasPermanentlyDelete: true,
                  date: elem.deleted_at,
                  deletedUser: (elem.deleted_by && elem.deleted_by.length > 0 ? elem.deleted_by : ''),
              });
          });
          this.show_trash(items, true);
      });
  }

  /**
   * Show Edit agency dialog and save after success close result
   * @param items
   * @param isAdmin
   */
  show_trash(items: any[], isAdmin = false) {
      let _title = this.translate.instant('ORGANISATION.LIST.DIALOG.SHOW_TRASH.TITLE');
      if (isAdmin) {
          _title = this.translate.instant('ORGANISATION.LIST.DIALOG.SHOW_TRASH.TITLE_ADMIN');
      }
      this.layoutUtilsService.trashedEntities(_title, items, this, isAdmin);
  }

  restore(id: number, isAdmin = false) {
      if (isAdmin) {
          this.store.dispatch(new OrganisationOnServerAdminRestored({id}));
      } else {
          this.store.dispatch(new OrganisationOnServerRestored({id}));
      }
  }

  delete(id: number) {
      this.store.dispatch(new OrganisationDeletedFromTrash({id}));
  }

  hardDelete(id: number) {
      this.store.dispatch(new OrganisationDeletedFromAdminTrash({id}));
  }

  flush() {
      
      this.store.dispatch(new OrganisationTrashFlushed());
  }

  viewEntry(item: OrganisationModel) {
    this.router.navigate(['view', item.id], {relativeTo: this.activatedRoute})
  }
  onHiddenChange(event: MatCheckboxChange, item: OrganisationModel) {
    const _item = cloneDeep(item)
    _item.is_hidden = event.checked
    this.store.dispatch(new OrganisationUpdated({
        item: _item,
        partialItem: {
            id: item.id,
            changes: {
                is_hidden: event.checked
            }
        }
    }))
  }
}
